import React, { useState } from 'react';
import { Table, TableRow, Tooltip, makeStyles } from '@material-ui/core';
import TableComponents from 'components/Table/';
import Tables from 'components/Table';
import Icons from 'components/Icons';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import { BoxWrapper, Button, Modal, Prompt } from 'components/Base';
import { Field, Formik } from 'formik';
import * as Yup from 'yup';
import Fields from 'components/Fields';
import api from 'api';
import { Notify } from 'utils';
import EmptyState from 'components/Base/EmptyState/EmptyState';
import BlankUser from 'assets/images/blankUser.png';

const useStyles = makeStyles({
  iconHolder: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    width: '36px',
    height: '48px'
  },
  btnContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '1rem',
    borderTop: '1px solid #eaeaea'
  }
});

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  job_title: Yup.string().nullable(),
  linkedin_url: Yup.string().url('Invalid URL').nullable(),
  profile_picture: Yup.mixed().nullable(),
  contact_number: Yup.string().nullable(),
  email: Yup.string().email('Invalid email').nullable()
});

interface KeyContactsProps {
  data: any;
  updateData: Function;
  classes: any;
}

interface KeyContactType {
  id?: number;
  name: string;
  job_title: string;
  contact_number: string;
  email: string;
  linkedin_url: string;
  profile_picture: string | null;
  profile_picture_url?: string | null;
}

const KeyContacts = ({ data, updateData, classes }: KeyContactsProps) => {
  classes = { ...classes, ...useStyles() };
  const [isSaving, setIsSaving] = useState(false);
  const [currentContact, setCurrentContact] = useState(
    null as KeyContactType | null
  );
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeletePromptOpen, setIsDeletePromptOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const closeModal = () => {
    setIsEditModalOpen(false);
    setCurrentContact(null);
  };

  const findKeyContactIndex = (id: number) => {
    return data.key_contacts.findIndex(
      (keyContact: KeyContactType) => keyContact.id === id
    );
  };

  const deleteKeyContact = (id: number) => {
    setIsDeleting(true);
    api
      .deleteKeyContact(data.id, id)
      .then(() => {
        const index = findKeyContactIndex(id);
        const keyContacts = data.key_contacts;
        keyContacts.splice(index, 1);
        updateData({ ...data, key_contacts: keyContacts });
        Notify.success('Key contact deleted successfully.');
      })
      .catch(() => {
        Notify.error('Key contact could not be deleted.');
      })
      .finally(() => {
        setIsDeleting(false);
        setIsDeletePromptOpen(false);
      });
  };

  return (
    <>
      {data.key_contacts.length > 0 ? (
        <Table className={classes.table}>
          <TableComponents.TableHead
            cells={['Name', 'Job title', 'Email', '', '']}
          />
          <TableComponents.TableBody>
            {data.key_contacts.map((keyContact: KeyContactType) => {
              return (
                <TableRow key={keyContact.id} style={{ cursor: 'pointer' }}>
                  <Tables.TableCell className={classes.cell}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <img
                        src={
                          keyContact.profile_picture_url
                            ? keyContact.profile_picture_url
                            : BlankUser
                        }
                        style={{
                          borderRadius: '50%',
                          border: '3px solid rgb(0, 128, 189)',
                          width: '50px',
                          height: '50px',
                          objectFit: 'cover',
                          marginRight: '0.5rem'
                        }}
                      />
                      <span>{keyContact.name}</span>
                    </div>
                  </Tables.TableCell>
                  <Tables.TableCell className={classes.cell}>
                    {keyContact.job_title}
                  </Tables.TableCell>
                  <Tables.TableCell className={classes.cell}>
                    {keyContact.email}
                  </Tables.TableCell>

                  <Tables.TableCell width="36px" style={{ padding: 0 }}>
                    <div
                      className={classes.iconHolder}
                      onClick={e => {
                        e.stopPropagation();
                        setCurrentContact(keyContact);
                        setIsEditModalOpen(true);
                      }}
                    >
                      <Tooltip title="Edit" arrow>
                        <Icons.EditIcon
                          width="2.2rem"
                          style={{ cursor: 'pointer' }}
                        />
                      </Tooltip>
                    </div>
                  </Tables.TableCell>
                  <Tables.TableCell width="36px" style={{ padding: 0 }}>
                    <div
                      className={classes.iconHolder}
                      onClick={e => {
                        e.stopPropagation();
                        setCurrentContact(keyContact);
                        setIsDeletePromptOpen(true);
                      }}
                    >
                      <Tooltip title="Delete" arrow>
                        <DeleteOutlineIcon
                          width="2.2rem"
                          style={{ cursor: 'pointer' }}
                        />
                      </Tooltip>
                    </div>
                  </Tables.TableCell>
                </TableRow>
              );
            })}
          </TableComponents.TableBody>
        </Table>
      ) : (
        <EmptyState
          title="No Key Contacts"
          description="Get started by adding a new Key Contact."
        />
      )}
      {!!currentContact && (
        <>
          <Modal isOpen={isEditModalOpen} closeModalFunc={closeModal}>
            <BoxWrapper
              title={`${currentContact.id ? 'Update' : 'Create'} Key Contact`}
              closeModal={closeModal}
            >
              <Formik
                enableReinitialize
                initialValues={
                  {
                    id: currentContact.id,
                    name: currentContact.name,
                    job_title: currentContact.job_title,
                    profile_picture: null,
                    contact_number: currentContact.contact_number,
                    email: currentContact.email,
                    linkedin_url: currentContact.linkedin_url
                  } as any
                }
                onSubmit={values => {
                  setIsSaving(true);

                  if (values.profile_picture === null) {
                    delete values.profile_picture;
                  }

                  const formData = new FormData();
                  for (const key in values) {
                    formData.append(key, values[key]);
                  }

                  let method = null;
                  const type = values.id ? 'update' : 'create';
                  if (type === 'create') {
                    method = api.createKeyContact(data.id, formData);
                  } else {
                    method = api.updateKeyContact(data.id, values.id, formData);
                  }

                  method
                    .then((response: any) => {
                      Notify.success(`Key contact ${type}d successfully.`);
                      const index = findKeyContactIndex(response.data.id);
                      const keyContacts = data.key_contacts;

                      if (index === -1) {
                        keyContacts.push(response.data);
                      } else {
                        keyContacts.splice(index, 1, response.data);
                      }

                      updateData({ ...data, key_contacts: keyContacts });
                    })
                    .catch(() => {
                      Notify.error(`Key contact could not be ${type}d.`);
                    })
                    .finally(() => {
                      setIsSaving(false);
                      closeModal();
                    });
                }}
                validationSchema={validationSchema}
              >
                {({ handleSubmit }) => {
                  return (
                    <form onSubmit={handleSubmit}>
                      <div className={classes.modalWrapper}>
                        <div className={classes.row}>
                          <div className={classes.label}>Name</div>
                          <div className={classes.fieldWrapper}>
                            <Field
                              name="name"
                              component={Fields.v2.Text}
                              filled
                            />
                          </div>
                        </div>
                        <div className={classes.row}>
                          <div className={classes.label}>Job title</div>
                          <div className={classes.fieldWrapper}>
                            <Field
                              name="job_title"
                              component={Fields.v2.Text}
                              filled
                            />
                          </div>
                        </div>
                        <div className={classes.row}>
                          <div className={classes.label}>Contact number</div>
                          <div className={classes.fieldWrapper}>
                            <Field
                              name="contact_number"
                              component={Fields.v2.Text}
                              filled
                            />
                          </div>
                        </div>
                        <div className={classes.row}>
                          <div className={classes.label}>Email address</div>
                          <div className={classes.fieldWrapper}>
                            <Field
                              name="email"
                              component={Fields.v2.Text}
                              filled
                            />
                          </div>
                        </div>
                        <div className={classes.row}>
                          <div className={classes.label}>LinkedIn URL</div>
                          <div className={classes.fieldWrapper}>
                            <Field
                              name="linkedin_url"
                              component={Fields.v2.Text}
                              filled
                            />
                          </div>
                        </div>
                        <div className={classes.row}>
                          <div className={classes.label}>Profile picture</div>
                          <div className={classes.fieldWrapper}>
                            <Field
                              name="profile_picture"
                              component={Fields.v2.FileUpload}
                              accept="image/*"
                              url={currentContact.profile_picture_url}
                            />
                          </div>
                        </div>
                      </div>
                      <div className={classes.btnContainer}>
                        <Button
                          key="cancel"
                          color="primary"
                          onClick={closeModal}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          disabled={isSaving}
                          isLoading={isSaving}
                        >
                          Save
                        </Button>
                      </div>
                    </form>
                  );
                }}
              </Formik>
            </BoxWrapper>
          </Modal>
          <Prompt
            isOpen={isDeletePromptOpen}
            title={`Are you sure you want to delete this key contact?`}
            confirmAction={() => deleteKeyContact(currentContact.id as number)}
            isProcessing={isDeleting}
            cancelAction={() => setIsDeletePromptOpen(false)}
          />
        </>
      )}

      <div style={{ display: 'flex' }}>
        <Button
          type="button"
          style={{
            margin: data.key_contacts.length === 0 ? '0 auto' : '1rem 0 0 0'
          }}
          onClick={() => {
            setCurrentContact({
              name: '',
              job_title: '',
              linkedin_url: '',
              profile_picture: null,
              contact_number: '',
              email: ''
            });
            setIsEditModalOpen(true);
          }}
        >
          Add new
        </Button>
      </div>
    </>
  );
};

export default KeyContacts;
